import { useEffect, useReducer, useRef, useState } from "react";
import styled from "styled-components";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { UserInvestmentPriority } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useEntryIntakeStore } from "@entry/store";
import { entryColors, entryFont, entryMotion } from "@entry/tokens";
import { EntryTitle } from "@entry/ui";
import { useErrorBanner, useOnboarding, useSetUserFinancialProfile, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { BalancedChart, MaximumChart, MinimumChart } from "./PreviewChart";
import {
  InvestmentPrioritySelectionAction,
  InvestmentPrioritySelectionState,
  InvestmentPrioritySelectionType,
} from "./types";

const initialState: InvestmentPrioritySelectionState = {
  minimum: false,
  balanced: false,
  maximum: false,
};

const reducer = (
  state: InvestmentPrioritySelectionState,
  action: InvestmentPrioritySelectionAction,
) => {
  return { ...initialState, [action.type]: !state[action.type] };
};

type IntroPhase = "hidden" | "entering" | "visible" | "leaving" | "done";

const DELAY_MS = 300;
const FADE_IN_MS = 800;
const HOLD_MS = 1500;
const FADE_OUT_MS = 400;

const IntroOverlay = styled.div<{ $phase: IntroPhase }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  padding-top: 0;
  pointer-events: ${({ $phase }) => ($phase === "done" ? "none" : "auto")};
  opacity: ${({ $phase }) => {
    if ($phase === "hidden") return 0;
    if ($phase === "entering" || $phase === "visible") return 1;
    return 0;
  }};
  transform: translateY(
    ${({ $phase }) => ($phase === "hidden" ? "40px" : "0")}
  );
  transition:
    opacity ${({ $phase }) => ($phase === "leaving" ? FADE_OUT_MS : FADE_IN_MS)}ms ease-out,
    transform ${({ $phase }) => ($phase === "leaving" ? "0" : FADE_IN_MS)}ms ease-out;
`;

const IntroTitle = styled.h1`
  margin: 0;
  font-family: ${entryFont.title};
  font-weight: 400;
  font-size: 28px;
  line-height: 34px;
  color: ${entryColors.textPrimary};

  span {
    color: ${entryColors.highlight};
  }
`;

const FormWrapper = styled.div<{ $visible: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? "0" : `${entryMotion.slideDistance}px`)});
  transition:
    opacity ${entryMotion.content.opacity}ms ${entryMotion.easing},
    transform ${entryMotion.content.transform}ms ${entryMotion.easing};
`;

const FormSubtitle = styled.p`
  margin: 18px 0 0;
  font-family: ${entryFont.body};
  font-size: 15px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.75);
`;

const OptionCard = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  height: 114px;
  overflow: hidden;
  padding: 16px 20px;
  border-radius: 10px;
  background: ${({ $selected }) =>
    $selected ? "rgba(205, 128, 84, 0.1)" : "rgba(244, 241, 242, 0.05)"};
  border: 2px solid ${({ $selected }) => ($selected ? "rgba(205, 128, 84, 0.8)" : "transparent")};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 24px rgba(205, 128, 84, 0.5)" : "none")};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  cursor: pointer;
  transition:
    background 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? "rgba(205, 128, 84, 0.14)" : "rgba(244, 241, 242, 0.1)"};
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 2px;
  }
`;

const OptionTitle = styled.div`
  font-family: ${entryFont.title};
  font-size: 18px;
  font-weight: 500;
  line-height: 18px;
  color: ${entryColors.highlight};
  margin-bottom: 4px;
`;

const OptionSubtitle = styled.div`
  font-family: ${entryFont.body};
  font-size: 13px;
  line-height: 18px;
  color: rgba(255, 255, 255, 0.6);
  width: 60%;
`;

const Disclaimer = styled.p`
  margin: 8px 0 0;
  font-family: ${entryFont.body};
  font-size: 11px;
  line-height: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

const ContentWrapper = styled.div`
  position: relative;
`;

export const InvestmentPriorityRoute = () => {
  const { navigateToNext } = useOnboarding();
  const [setUserFinancialProfile, { loading }] = useSetUserFinancialProfile();
  const { showErrorBanner } = useErrorBanner();

  const { user } = useUser();
  const { intake } = useEntryIntakeStore();
  const displayName =
    user?.preferredName || intake.preferredName || user?.firstName || intake.firstName;

  const [introPhase, setIntroPhase] = useState<IntroPhase>("hidden");
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const schedule = (fn: () => void, delay: number) => {
      timers.current.push(setTimeout(fn, delay));
    };

    schedule(() => setIntroPhase("entering"), DELAY_MS);
    schedule(() => setIntroPhase("visible"), DELAY_MS + FADE_IN_MS);
    schedule(() => setIntroPhase("leaving"), DELAY_MS + FADE_IN_MS + HOLD_MS);
    schedule(() => setIntroPhase("done"), DELAY_MS + FADE_IN_MS + HOLD_MS + FADE_OUT_MS);

    return () => {
      timers.current.forEach(clearTimeout);
    };
  }, []);

  const formVisible = introPhase === "done";

  const [{ maximum, minimum, balanced }, dispatch] = useReducer(reducer, initialState);

  const investmentPriority = (() => {
    if (minimum) return UserInvestmentPriority.AvoidLosses;
    if (balanced) return UserInvestmentPriority.BalanceRisk;
    if (maximum) return UserInvestmentPriority.MaximizeReturns;
    return undefined;
  })();

  const handleSubmit = async () => {
    try {
      await setUserFinancialProfile({
        variables: {
          investmentPriority,
        },
      });

      navigateToNext();
    } catch {
      showErrorBanner();
    }
  };

  const selectMinimum = () => {
    dispatch({ type: InvestmentPrioritySelectionType.Minimum });
  };

  const selectBalanced = () => {
    dispatch({ type: InvestmentPrioritySelectionType.Balanced });
  };

  const selectMaximum = () => {
    dispatch({ type: InvestmentPrioritySelectionType.Maximum });
  };

  const props: OnboardingLayoutProps = {
    cta: formVisible
      ? {
          primary: {
            title: i18n.t(Translations.InvestmentPriorityRoutePrimaryCTA),
            disabled: !investmentPriority,
            onClick: handleSubmit,
            loading,
          },
        }
      : undefined,
    footer: formVisible ? (
      <Disclaimer>{i18n.t(Translations.InvestmentPriorityRouteDisclaimer)}</Disclaimer>
    ) : undefined,
  };

  return (
    <OnboardingLayout {...props}>
      <ContentWrapper>
        {introPhase !== "done" && (
          <IntroOverlay $phase={introPhase}>
            <IntroTitle>
              {(() => {
                const text = i18n.t(Translations.InvestmentPriorityRouteIntroTitle, {
                  name: displayName,
                });
                if (!displayName) return text;
                const idx = text.indexOf(displayName);
                if (idx === -1) return text;
                return (
                  <>
                    {text.slice(0, idx)}
                    <span>{displayName}</span>
                    {text.slice(idx + displayName.length)}
                  </>
                );
              })()}
            </IntroTitle>
          </IntroOverlay>
        )}

        <FormWrapper $visible={formVisible}>
          <EntryTitle $size={34}>
            {i18n.t(Translations.InvestmentPriorityRouteTitle)}
          </EntryTitle>
          <FormSubtitle>
            {i18n.t(Translations.InvestmentPriorityRouteSubtitle)}
          </FormSubtitle>
          <Spacing size={SpacingSizes.M} />
          <OptionCard
            type="button"
            role="radio"
            aria-checked={minimum}
            $selected={minimum}
            onClick={selectMinimum}
          >
            <OptionTitle>
              {i18n.t(Translations.InvestmentPriorityRouteMinimizeTitle)}
            </OptionTitle>
            <OptionSubtitle>
              {i18n.t(Translations.InvestmentPriorityRouteMinimizeSubtitle)}
            </OptionSubtitle>
            <MinimumChart />
          </OptionCard>

          <Spacing size={SpacingSizes.M} />

          <OptionCard
            type="button"
            role="radio"
            aria-checked={balanced}
            $selected={balanced}
            onClick={selectBalanced}
          >
            <OptionTitle>
              {i18n.t(Translations.InvestmentPriorityRouteBalancedTitle)}
            </OptionTitle>
            <OptionSubtitle>
              {i18n.t(Translations.InvestmentPriorityRouteBalancedSubtitle)}
            </OptionSubtitle>
            <BalancedChart />
          </OptionCard>

          <Spacing size={SpacingSizes.M} />

          <OptionCard
            type="button"
            role="radio"
            aria-checked={maximum}
            $selected={maximum}
            onClick={selectMaximum}
          >
            <OptionTitle>
              {i18n.t(Translations.InvestmentPriorityRouteMaximizedTitle)}
            </OptionTitle>
            <OptionSubtitle>
              {i18n.t(Translations.InvestmentPriorityRouteMaximizedSubtitle)}
            </OptionSubtitle>
            <MaximumChart />
          </OptionCard>

          <Spacing size={SpacingSizes.M} />
        </FormWrapper>
      </ContentWrapper>
    </OnboardingLayout>
  );
};
